// src/common/credits.js
/**
 * Credit management utilities for fetching and displaying credit balance.
 */

/**
 * Fetch credit balance from the API.
 * 
 * @param {string} apiBase - Base URL of the API
 * @param {string} accessToken - Bearer token for authentication
 * @returns {Promise<Object>} Credit balance response
 */
export async function fetchCreditBalance(apiBase, accessToken) {
  try {
    const response = await fetch(`${apiBase}/credits/balance`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        // User has no credit record yet - return unlimited placeholder
        return {
          balance_cents: 0,
          limit_cents: 0,
          spent_cents: 0,
          balance_pct: 100,
          warning_threshold_pct: 80,
          is_warning: false,
          is_blocked: false,
        };
      }
      console.warn('[CREDITS] Failed to fetch credit balance:', response.status);
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.error('[CREDITS] Error fetching credit balance:', error);
    return null;
  }
}

/**
 * Update the credit display in the UI.
 * 
 * @param {number} balanceCents - Remaining balance in cents
 * @param {number} limitCents - Credit limit in cents
 * @param {number} warningThresholdPct - Warning threshold percentage
 */
export function updateCreditDisplay(balanceCents, limitCents, warningThresholdPct = 80) {
  const displayEl = document.getElementById('credit-display');
  const amountEl = document.getElementById('credit-amount');
  const fillEl = document.getElementById('credit-bar-fill');
  const loadingEl = document.getElementById('credit-loading');
  
  // Hide loading indicator
  if (loadingEl) loadingEl.style.display = 'none';
  
  // If no credit limit set (0 or null), hide the display
  if (!limitCents || limitCents <= 0) {
    if (displayEl) displayEl.style.display = 'none';
    return;
  }
  
  // Show the credit display (use block for sidebar layout)
  if (displayEl) displayEl.style.display = 'flex';
  
  const balanceUsd = (balanceCents / 100).toFixed(2);
  const balancePct = Math.max(0, Math.min(100, (balanceCents / limitCents) * 100));
  const remainingPct = 100 - (warningThresholdPct || 80);
  
  // Update amount text
  if (amountEl) {
    amountEl.textContent = `$${balanceUsd}`;
    
    // Apply warning/low classes
    amountEl.classList.remove('warning', 'low');
    if (balancePct <= 10) {
      amountEl.classList.add('low');
    } else if (balancePct <= remainingPct) {
      amountEl.classList.add('warning');
    }
  }
  
  // Update progress bar
  if (fillEl) {
    fillEl.style.width = `${balancePct}%`;
    
    // Apply warning/low classes
    fillEl.classList.remove('warning', 'low');
    if (balancePct <= 10) {
      fillEl.classList.add('low');
    } else if (balancePct <= remainingPct) {
      fillEl.classList.add('warning');
    }
  }
}

/**
 * Initialize credit display.
 * 
 * No auto-refresh polling - call refresh() manually after operations complete.
 * 
 * @param {string} apiBase - Base URL of the API
 * @param {Function} getAccessToken - Function that returns current access token
 * @returns {Object} Control object with refresh() method
 */
export function initCreditDisplay(apiBase, getAccessToken) {
  async function refresh() {
    const token = await getAccessToken();
    if (!token) {
      console.log('[CREDITS] No access token, skipping refresh');
      return null;
    }
    
    const balance = await fetchCreditBalance(apiBase, token);
    if (balance) {
      updateCreditDisplay(
        balance.balance_cents,
        balance.limit_cents,
        balance.warning_threshold_pct
      );
    }
    return balance;
  }
  
  // Initial fetch on load
  refresh();
  
  // Wire up usage button if present
  const usageBtn = document.getElementById('btn-usage');
  if (usageBtn) {
    usageBtn.addEventListener('click', () => {
      // Dispatch event to open usage modal
      window.dispatchEvent(new CustomEvent('open-usage-modal'));
    });
  }
  
  // Store refresh function globally so it can be called after operations
  window.refreshCredits = refresh;
  
  return {
    refresh,
  };
}

